import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import * as Tooltip from '@radix-ui/react-tooltip';
import { MainButton, SecondaryButton } from '../../../common';
import {
	faAngleLeft,
	faAngleRight,
	faCaretRight,
} from '@fortawesome/free-solid-svg-icons';
import AlertDialogConfirm from '../AlertDialogConfirm/AlertDialogConfirm';

export interface Step {
	key: string;
	label: string;
	icon: IconProp;
	color: string; // couleur étape active
	textColor?: string; // couleur texte uniquement
	side?: boolean; // sortie hors parcours (annulation, report)
}

export interface Transition {
	label: string;
	to: string;
	type: 'main' | 'secondary'; // main = bouton droit, secondary = bouton gauche
	alert?: string; // message d'alerte avant changement
}

export interface StatusConfig {
	steps: Step[];
	transitions: Record<string, Transition[]>;
}

/** Place les cibles de gauche à droite selon les étapes, et oriente le chevron. */
export function orderTransitions(
	steps: Step[],
	from: string,
	transitions: Transition[]
): Transition[] {
	const indexOf = (key: string) => {
		const index = steps.findIndex((step) => step.key === key);
		return index === -1 ? steps.length : index;
	};
	const fromIndex = indexOf(from);

	return [...transitions]
		.sort((a, b) => indexOf(a.to) - indexOf(b.to))
		.map((transition) => ({
			...transition,
			type: indexOf(transition.to) < fromIndex ? 'secondary' : 'main',
		}));
}

interface StatusCardProps {
	status: string | undefined;
	config: StatusConfig;
	onStatusChange: (newStatus: string) => void;
	icon?: IconProp; // icône devant le titre
	className?: string;
	size?: 'sm' | 'md' | 'lg';
	loading?: boolean;
}

const StatusCard: React.FC<StatusCardProps> = ({
	status,
	config,
	onStatusChange,
	className = '',
	size = 'lg',
	loading = false,
}) => {
	// Déterminer l'étape actuelle
	const currentStepIndex = config.steps.findIndex((s) => s.key === status);
	const activeStep = config.steps.find((s) => s.key === status);

	// Gestion du state pour AlertDialogConfirm
	const [isAlertOpen, setIsAlertOpen] = React.useState(false);
	const [isAlertConfirmed, setIsAlertConfirmed] = React.useState(false);
	const [pendingStatus, setPendingStatus] = React.useState<string | undefined>(
		undefined
	);
	const [pendingMessage, setPendingMessage] = React.useState<
		string | undefined
	>(undefined);

	// handle pour ouvrir l'alerte ou changer directement le statut
	const onStatusChangeHandler = (newStatus: string) => {
		if (status === undefined) return;

		const transition = config.transitions[status]?.find(
			(t) => t.to === newStatus
		);
		if (transition?.alert) {
			setPendingStatus(newStatus);
			setPendingMessage(transition.alert);
			setIsAlertConfirmed(false);
			setIsAlertOpen(true);
		} else {
			onStatusChange(newStatus);
		}
	};

	// handle pour confirmer le changement de statut après alerte
	React.useEffect(() => {
		if (isAlertConfirmed) {
			if (pendingStatus) {
				onStatusChange(pendingStatus);
				setPendingStatus(undefined);
			}
			setIsAlertConfirmed(false);
			setIsAlertOpen(false);
		} else {
			setIsAlertConfirmed(false);
		}
	}, [isAlertOpen]);

	if (loading || !status) {
		// Skeleton loader
		return (
			<div
				className={`${size === 'lg' ? 'bg-white' : 'bg-gray-50'} p-6 rounded-lg shadow-md h-full animate-pulse ${className}`}
			>
				<h2
					className={`
				${size === 'lg' ? 'text-2xl font-bold' : 'text-xl font-bold'}
				 mb-4 flex items-center`}
				>
					<FontAwesomeIcon
						icon={faCaretRight}
						className="w-6 h-6 text-gray-700"
					/>{' '}
					État : <span className="h-8 w-40 bg-gray-300 ml-2 rounded" />
				</h2>
				<div className="flex items-center justify-between">
					{config.steps.map((step) => (
						<div
							key={step.key}
							className="flex-1 flex flex-col items-center w-12"
						>
							<div className="w-12 h-12 bg-gray-300 rounded-full mb-2" />
						</div>
					))}
				</div>
				<div className="flex gap-2 mt-2">
					{Array(2)
						.fill(0)
						.map((_, i) => (
							<div key={i} className="flex-1 h-7 bg-gray-300 rounded" />
						))}
				</div>
			</div>
		);
	}

	const transitions = orderTransitions(
		config.steps,
		status,
		config.transitions[status] || []
	);
	const onSidePath = activeStep?.side === true;

	return (
		<div
			className={`${size === 'lg' ? 'bg-white' : 'bg-gray-50'} p-6 rounded-lg shadow-md h-full ${className}`}
		>
			<h2
				className={`
				${size === 'lg' ? 'text-2xl font-bold' : 'text-xl font-bold'}
				 mb-4 flex items-center`}
			>
				<FontAwesomeIcon
					icon={faCaretRight}
					className="w-6 h-6 text-gray-700"
				/>{' '}
				État :{' '}
				<span className={`ml-2 font-semibold ${activeStep?.textColor}`}>
					{' '}
					{activeStep?.label}
				</span>
			</h2>

			{/* Stepper */}
			<Tooltip.Provider delayDuration={200}>
				<div className="flex items-center justify-between mb-4 mt-4">
					{config.steps.map((step, index, arr) => {
						const isActive = status === step.key;
						const isCompleted =
							step.side !== true && !onSidePath && currentStepIndex > index;
						const color = isActive
							? step.color
							: isCompleted
								? 'bg-blue-300 text-gray-200'
								: 'bg-gray-100 text-gray-400';
						const clickable =
							!isActive && transitions.some((item) => item.to === step.key);

						const circleClass = `w-12 h-12 rounded-full flex items-center justify-center text-2xl ${color} ${
							clickable
								? 'cursor-pointer hover:ring-2 hover:ring-offset-2 hover:ring-blue-400'
								: 'cursor-default'
						}`;
						const circle = clickable ? (
							<button
								type="button"
								aria-label={step.label}
								onClick={() => onStatusChangeHandler(step.key)}
								className={circleClass}
							>
								<FontAwesomeIcon icon={step.icon} />
							</button>
						) : (
							<div aria-label={step.label} className={circleClass}>
								<FontAwesomeIcon icon={step.icon} />
							</div>
						);

						return (
							<React.Fragment key={step.key}>
								<div className="flex flex-col items-center">
									<Tooltip.Root>
										<Tooltip.Trigger asChild>{circle}</Tooltip.Trigger>
										<Tooltip.Content
											side="top"
											className="z-50 bg-gray-800 text-white text-xs rounded px-2 py-1"
										>
											{step.label}
											<Tooltip.Arrow className="fill-gray-800" />
										</Tooltip.Content>
									</Tooltip.Root>
								</div>
								{index < arr.length - 1 && (
									<div
										className={`flex-1 h-1 ${isCompleted ? 'bg-blue-300' : 'bg-gray-200'}`}
									/>
								)}
							</React.Fragment>
						);
					})}
				</div>
			</Tooltip.Provider>

			{/* Boutons */}
			<div className="flex w-full mt-4 gap-2">
				{transitions.map((t, i) => {
					const Btn = t.type === 'main' ? MainButton : SecondaryButton;
					const targetStep = config.steps.find((step) => step.key === t.to);
					const iconChevron = t.type === 'main' ? faAngleRight : faAngleLeft;
					const icon = targetStep?.side ? targetStep.icon : iconChevron;
					const invert = t.type === 'main';

					// si deux boutons, chaque moitié, sinon position à gauche ou droite
					let wrapperClass = 'flex-1';
					if (transitions.length === 2) wrapperClass = 'w-1/2';
					else if (transitions.length === 1)
						wrapperClass =
							t.type === 'main' ? 'ml-auto w-1/2' : 'mr-auto w-1/2';

					return (
						<div className={wrapperClass} key={i}>
							<Btn
								fullWidth
								label={t.label}
								size="sm"
								icon={icon}
								onClick={() => onStatusChangeHandler(t.to)}
								invert={invert}
								forceShowLabel={true}
							/>
						</div>
					);
				})}
			</div>

			<AlertDialogConfirm
				open={isAlertOpen}
				setOpen={setIsAlertOpen}
				setConfirmed={setIsAlertConfirmed}
				text={
					pendingMessage || 'Etes-vous sûr de vouloir effectuer ce changement ?'
				}
			/>
		</div>
	);
};

export default StatusCard;
